import { useEffect, useState } from "react";
import { Groupper, Input, Field, Button, Message, Table, Icon, Label, Modal, Header, Toast, Toolbar } from "react-frontier";
import { UserClass, EventDateList, SetLoading, Budget, ExpenseRejectReason } from '../SiteClasses';
import { SearchUserModal } from "../components";
import SearchCoordinatorEventModal from "../components/SearchCoordinatorEventModal";
import { Dropdown } from 'semantic-ui-react';
import { useBreadcrumbs, useUser } from "../hooks";
import { addCommas, bindClick, getDepartmentName, formatCreatorName } from "../SiteUtil";
import UserAccess from '../UserAccess';
import classNames from 'classnames';

import API from "../API";

enum Modals{
	USER = 1,
	EVENT = 2,
}

enum DetailModals{
	BUDGET_INFO = 1,
	REJECT = 2,
	APPROVE_ALL_CONFIRM = 4,
	PAID = 5,
	PAID_ALL_CONFIRM = 6,
}

var BudgetsDetails = ()=>{
	var { setTitle } = useBreadcrumbs();
	var { hasAccess } = useUser();
	var [selectedUser, setSelectedUser] = useState<UserClass>(null);
	var [selectedDate, setSelectedDate] = useState<EventDateList>(null);
	var [details, setDetails] = useState<Budget[]>(null);
	var [shownModal, setShownModal] = useState<Modals>(null);
	var [detailModal, setDetailModal] = useState<DetailModals>(null);
	var [errorPrompt, setErrorPrompt] = useState<string[]>([]);
	var [selectedBudget, setSelectedBudget] = useState<Budget>(null);
	var [rejectReasonId, setRejectReasonId] = useState<number>(null);
	var [rejectReasons, setRejectReasons] = useState<ExpenseRejectReason[]>([]);
	var [modalPrompts, setModalPrompts] = useState<string[]>(null);
	var [loadingAction, setLoadingAction] = useState<boolean>(false);
	var [selectedApproveIds, setSelectedApproveIds] = useState<number[]>([]);
	var [selectedPaidIds, setSelectedPaidIds] = useState<number[]>([]);

	useEffect(()=>{
		setTitle('Desglose de presupuestos');
		API.getExpenseRejectReasons().then(res=>{
			if(!res.error) setRejectReasons(res.data);
		});
	}, [])

	var getDetails = (setLoading: SetLoading)=>{
		if(!selectedUser || !selectedDate) return setErrorPrompt(['Selecciona un coordinador y un evento.']);

		setLoading(true);
		API.getCoordinatorBusdgetsDetails(selectedUser.user_id, selectedDate.date_id).then(details=>{
			if(details.error) return setErrorPrompt([details.message]);
			setDetails(details.data);
		}).catch(err=>{
			return setErrorPrompt(['Hubo un error al obtener los detalles de presupuestos. (BDD-1)']);
		}).finally(()=>{
			setErrorPrompt([]);
			setLoading(false);
		});
	}

	var rejectBudget = ()=>{
		if(!rejectReasonId)return setModalPrompts(['Selecciona una razón de rechazo.']);
		setLoadingAction(true);

		API.rejectBudget(selectedBudget.budget_id, rejectReasonId).then(res=>{
			if(res.error) return setModalPrompts([res.message]);

			Toast.success('Se ha rechazado el presupuesto');
			var reasonName = rejectReasons.find(r => r.reason_id === rejectReasonId)?.reason;
			setDetails(prev => prev.map(b => b.budget_id === selectedBudget.budget_id ? { ...b, rejected: true, approved: false, reject_reason_name: reasonName } : b));
			setDetailModal(null);
			setSelectedBudget(null);
			setRejectReasonId(null);
		}).catch(err=>{
			return setModalPrompts(['Hubo un error al rechazar el presupuesto. (BDD-2)']);
		}).finally(()=>{
			setLoadingAction(false);
		});
	}

	var onApproveAllClick = ()=>{
		var pending = details ? details.filter(b => !b.approved && !b.rejected && !b.deleted) : [];
		setSelectedApproveIds(pending.map(b => b.budget_id));
		setModalPrompts(null);
		setDetailModal(DetailModals.APPROVE_ALL_CONFIRM);
	}

	var approveBudget = ()=>{
		setLoadingAction(true);
		API.approveBudget([selectedBudget.budget_id]).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			Toast.success('Se ha aprobado el presupuesto');
			setDetails(prev => prev.map(b => b.budget_id === selectedBudget.budget_id ? { ...b, approved: true } : b));
			setDetailModal(null);
			setSelectedBudget(null);
		}).catch(err=>{
			return setModalPrompts(['Hubo un error al aprobar el presupuesto. (BDD-3)']);
		}).finally(()=>{
			setLoadingAction(false);
		});
	}

	var approveAllBudgets = ()=>{
		if(selectedApproveIds.length === 0){
			setModalPrompts(['Selecciona al menos un presupuesto.']);
			return;
		}
		setLoadingAction(true);
		API.approveBudget(selectedApproveIds).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			Toast.success('Se han aprobado los presupuestos');
			setDetails(prev => prev.map(b => selectedApproveIds.includes(b.budget_id) ? { ...b, approved: true } : b));
			setDetailModal(null);
			setSelectedApproveIds([]);
		}).catch(err=>{
			return setModalPrompts(['Hubo un error al aprobar los presupuestos. (BDD-4)']);
		}).finally(()=>{
			setLoadingAction(false);
		});
	}

	var paid = ()=>{
		setLoadingAction(true);
		API.paidBudget(selectedBudget.budget_id).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			Toast.success('Se ha marcado el presupuesto como pagado');
			setDetails(prev => prev.map(b => b.budget_id === selectedBudget.budget_id ? { ...b, paid: true } : b));
			setDetailModal(null);
			setSelectedBudget(null);
		}).catch(err=>{
			return setModalPrompts(['Hubo un error al marcar el presupuesto como pagado. (BDD-5)']);
		}).finally(()=>{
			setLoadingAction(false);
		});
	}

	var onPaidAllClick = ()=>{
		var payable = details ? details.filter(b => b.approved && !b.paid && !b.rejected && !b.deleted) : [];
		setSelectedPaidIds(payable.map(b => b.budget_id));
		setModalPrompts(null);
		setDetailModal(DetailModals.PAID_ALL_CONFIRM);
	}

	var paidAllBudgets = ()=>{
		if(selectedPaidIds.length === 0){
			setModalPrompts(['Selecciona al menos un presupuesto.']);
			return;
		}
		setLoadingAction(true);
		var promises = selectedPaidIds.map(id => API.paidBudget(id));
		Promise.all(promises).then(results=>{
			var failed = results.filter(r => r.error);
			if(failed.length > 0){
				setModalPrompts([`${failed.length} presupuesto(s) no se pudieron marcar como pagados.`]);
				return;
			}
			Toast.success('Se han marcado los presupuestos como pagados');
			setDetails(prev => prev.map(b => selectedPaidIds.includes(b.budget_id) ? { ...b, paid: true } : b));
			setDetailModal(null);
			setSelectedPaidIds([]);
		}).catch(err=>{
			return setModalPrompts(['Hubo un error al marcar los presupuestos como pagados. (BDD-6)']);
		}).finally(()=>{
			setLoadingAction(false);
		});
	}

	var pendingCount = details ? details.filter(b=>!b.approved && !b.rejected && !b.deleted).length : 0;
	var payableCount = details ? details.filter(b=>b.approved && !b.paid && !b.rejected && !b.deleted).length : 0;
	var budgetsTotal = details ? details.reduce((sum, d)=>sum + parseFloat(d.amount.toString()), 0) : 0;
	var payableTotal = details ? details.filter(b=>b.approved && !b.paid && !b.rejected && !b.deleted).reduce((sum, d)=>sum + parseFloat(d.amount.toString()), 0) : 0;
	var pendingBudgets = details ? details.filter(b => !b.approved && !b.rejected && !b.deleted) : [];
	var payableBudgets = details ? details.filter(b => b.approved && !b.paid && !b.rejected && !b.deleted) : [];
	var selectedApproveTotal = pendingBudgets.filter(b => selectedApproveIds.includes(b.budget_id)).reduce((sum, b) => sum + parseFloat(b.amount.toString()), 0);
	var selectedPaidTotal = payableBudgets.filter(b => selectedPaidIds.includes(b.budget_id)).reduce((sum, b) => sum + parseFloat(b.amount.toString()), 0);

	return <div>
		<Groupper title={'Desglose de presupuestos'} width={500} style={{ marginTop: 20 }} actions={<Button text="Buscar" color="black" onClick={getDetails}/>}>
			<Field label='Coordinador' style={{ marginTop: 10 }}>
			<Input
				value={selectedUser?.name}
				placeholder='Selecciona un coordinador...'
				readonly
				button={
				<Button
					text='Seleccionar'
					onClick={bindClick(setShownModal, Modals.USER)}
				/>
				}
			/>
			</Field>							
			{selectedUser && <Field label='Evento' style={{ marginTop: 10 }}>
				<Input
					value={selectedDate?.event_name}
					placeholder='Selecciona un evento...'
					readonly
					button={
					<Button
						text='Seleccionar'
						onClick={bindClick(setShownModal, Modals.EVENT)}
				/>
				}
			/>
			</Field>}
			<Message list={errorPrompt} type="error" style={{ marginTop: 20 }}/>
		</Groupper>
		{details && details.length === 0 && (
			<Header text='No se encontraron registros' size="small" iconName='face-sad-tear' style={{ marginTop: 100, marginBottom: 50 }} centered />
		)}
		{details && details.length > 0 && <Table
			striped
			title="Desglose"
			style={{ margin: 'auto', marginTop: 40, maxWidth: 900 }}
			headers={['ID', 'Titulo', 'Creador', 'Tipo', 'Departamento', 'Estatus', 'Monto', <Icon name='wrench' />]}
			collapsingIndexes={[0, 1, 2, 3, 4, 5, 6, 7]}
			centeredIndexes={[3, 7]}
			button={(pendingCount > 0 || payableCount > 0) ? (
				<Dropdown icon={null} pointing direction='left' trigger={(
					<Button text='Opciones' color='black' iconRight='caret-down' iconRightStyle={{ marginRight: 0, marginLeft: 5 }} size='small'/>
				)}>
					<Dropdown.Menu>
						{pendingCount > 0 && hasAccess(UserAccess.EVENTS.APPROVE_EXPENSE) ? (
							<Dropdown.Item text='Aprobar todos' icon='check' onClick={onApproveAllClick}/>
						) : null}
						{payableCount > 0 && hasAccess(UserAccess.ACCOUNTING.PAY_EXPENSE) ? (
							<Dropdown.Item text='Pagar todos' icon='check circle' onClick={onPaidAllClick}/>
						) : null}
					</Dropdown.Menu>
				</Dropdown>
			) : null}
		>
			{details.map(b => (
				<Table.Row
					collapsingIndexes={[0, 1, 2, 3, 4, 5, 6, 7]}
					centeredIndexes={[3, 7]}
					key={b.budget_id}
					data={[
						b.budget_id,
						b.budget_title,
						formatCreatorName(b.user_creator_name),
						b.type_name,
						b.department_id ? <Label style={{ width: 80, textAlign: 'center', padding: 3, fontSize: 11 }} color='orange'>{getDepartmentName(b.department_id)}</Label> : <Icon name='minus'/>,
						<Label style={{ width: 80, textAlign: 'center', padding: '2px 4px', fontSize: 11 }} color={b.deleted ? 'red' : b.rejected ? 'red' : b.paid ? 'green' : b.approved ? 'green' : 'yellow'}>
							{b.deleted ? 'Eliminado' : b.rejected ? 'Rechazado' : b.paid ? 'Pagado' : b.approved ? 'Aprobado' : 'Pendiente'}
						</Label>,
						'$' + addCommas(b.amount),
						<Dropdown direction='left' icon={(
							<div className="fr label" style={{ padding: '3px 10px' }}>
								<Icon name='ellipsis' style={{ margin: 0 }} />
							</div>
						)} className='icon'>
							<Dropdown.Menu direction='left'>
								<Dropdown.Item text='Info' icon='eye' onClick={()=>{
									setSelectedBudget(b);
									setModalPrompts(null);
									setDetailModal(DetailModals.BUDGET_INFO);
								}}/>
								{!b.approved && !b.rejected && !b.deleted && hasAccess(UserAccess.EVENTS.APPROVE_EXPENSE) ? (
									<Dropdown.Item text='Aprobar' icon='check' onClick={()=>{
										setSelectedBudget(b);
										setModalPrompts(null);
										setDetailModal(DetailModals.BUDGET_INFO);
									}}/>
								) : null}
								{!b.rejected && !b.deleted && !b.paid && hasAccess(UserAccess.EVENTS.REJECT_EXPENSE) ? (
									<Dropdown.Item text='Rechazar' icon='ban' onClick={()=>{
										setSelectedBudget(b);
										setRejectReasonId(null);
										setModalPrompts(null);
										setDetailModal(DetailModals.REJECT);
									}}/>
								) : null}
								{!b.deleted && !b.paid && b.approved && !b.rejected && hasAccess(UserAccess.ACCOUNTING.PAY_EXPENSE) ? (
									<Dropdown.Item text='Marcar pagado' icon='check circle' onClick={()=>{
										setSelectedBudget(b);
										setModalPrompts(null);
										setDetailModal(DetailModals.PAID);
									}}/>
								) : null}
							</Dropdown.Menu>
						</Dropdown>,
					]}
				/>
			))}
			<Table.Row>
				<Table.Cell colSpan={6} style={{ textAlign: 'right', fontSize: 14, fontWeight: 'bold' }}>
					Total:
				</Table.Cell>
				<Table.Cell collapsing colSpan={2} style={{ textAlign: 'start', fontSize: 14, fontWeight: 'bold' }}>
					${addCommas(budgetsTotal)}
				</Table.Cell>
			</Table.Row>
		</Table>}
		<SearchCoordinatorEventModal
			open={shownModal===Modals.EVENT}
			onClose={()=>setShownModal(null)}
			coordinatorId={selectedUser?.user_id}
			onSelected={(e)=>{
				setSelectedDate(e);
				setShownModal(null);
			}}
		/>
		<SearchUserModal open={shownModal===Modals.USER} onClose={()=>setShownModal(null)} onSelected={(u)=>{
			setSelectedUser(u);
			setSelectedDate(null);
			setDetails(null);
			setShownModal(null);
		}}/>
		<Modal open={detailModal===DetailModals.BUDGET_INFO} size='tiny' onClose={()=>{setDetailModal(null); setSelectedBudget(null);}}>
			<Modal.Header>Información de presupuesto</Modal.Header>
			<Toolbar stretch style={{ height: 40 }}>
				{selectedBudget && !selectedBudget.approved && !selectedBudget.rejected && !selectedBudget.deleted && hasAccess(UserAccess.EVENTS.APPROVE_EXPENSE) ? (
					<Toolbar.Item text='Aprobar' iconName='check' onClick={()=>{ setModalPrompts(null); approveBudget(); }}/>
				) : null}
				{selectedBudget && !selectedBudget.rejected && !selectedBudget.deleted && !selectedBudget.paid && hasAccess(UserAccess.EVENTS.REJECT_EXPENSE) ? (
					<Toolbar.Item text='Rechazar' iconName='ban' onClick={()=>{ setRejectReasonId(null); setModalPrompts(null); setDetailModal(DetailModals.REJECT); }}/>
				) : null}
				{selectedBudget && !selectedBudget.deleted && !selectedBudget.paid && !!selectedBudget.approved && !selectedBudget.rejected && hasAccess(UserAccess.ACCOUNTING.PAY_EXPENSE) ? (
					<Toolbar.Item text='Marcar pagado' iconName='check-circle' onClick={()=>{ setModalPrompts(null); setDetailModal(DetailModals.PAID); }}/>
				) : null}
			</Toolbar>
			{selectedBudget && <Modal.Content style={{ paddingBottom: 0 }}>
				<Table
					striped
					fitted
					details
					data={[
						['ID', selectedBudget.budget_id],
						['Título', selectedBudget.budget_title],
						['Tipo', selectedBudget.type_name],
						['Departamento', selectedBudget.department_id ? <Label style={{ width: 110 }} color='orange'>{getDepartmentName(selectedBudget.department_id)}</Label> : <Icon name='minus'/>],
						['Creador', selectedBudget.user_creator_name || <Icon name='minus'/>],
						...(selectedBudget.date_id ? [['Evento', selectedBudget.event_name || <Icon name='minus'/>]] : []),
						['Monto', '$' + addCommas(selectedBudget.amount)],
						['Estatus', <div className={classNames("fr label", {
							blue: selectedBudget.approved && !selectedBudget.deleted && !selectedBudget.rejected && !selectedBudget.paid,
							green: selectedBudget.paid && !selectedBudget.deleted,
							red: selectedBudget.deleted || selectedBudget.rejected,
							yellow: !selectedBudget.approved && !selectedBudget.deleted && !selectedBudget.rejected && !selectedBudget.paid
						})}>
							{selectedBudget.deleted ? 'Eliminado' : selectedBudget.rejected ? 'Rechazado' : selectedBudget.paid ? 'Pagado' : selectedBudget.approved ? 'Aprobado' : 'Pendiente'}
						</div>],
						...(selectedBudget.rejected ? [['Razón de rechazo', selectedBudget.reject_reason_name || <Icon name='minus'/>]] : []),
						['Descripción', selectedBudget.description || <Icon name='minus'/>],
					]}
				/>
				<Message type="error" list={modalPrompts} />
			</Modal.Content>}
			<Modal.Actions>
				<Button text='Cerrar' basic onClick={()=>{setDetailModal(null); setSelectedBudget(null);}} />
			</Modal.Actions>
		</Modal>
		<Modal open={detailModal===DetailModals.REJECT} size='tiny' onClose={()=>setDetailModal(DetailModals.BUDGET_INFO)}>
			<Modal.Header>¿Rechazar presupuesto?</Modal.Header>
			{selectedBudget && <Modal.Content>
				<Header text='¿Estás seguro de que deseas rechazar este presupuesto?' />
				<Table
					striped
					details
					data={[
						['ID', selectedBudget.budget_id],
						['Título', selectedBudget.budget_title],
						['Tipo', selectedBudget.type_name],
						['Departamento', selectedBudget.department_id ? <Label style={{ width: 110 }} color='orange'>{getDepartmentName(selectedBudget.department_id)}</Label> : <Icon name='minus'/>],
						['Monto', '$' + addCommas(selectedBudget.amount)],
					]}
				/>
				<Dropdown
					selection
					fluid
					placeholder='Selecciona el motivo de rechazo'
					value={rejectReasonId}
					onChange={(e, { value })=>setRejectReasonId(value as number)}
					options={rejectReasons.map(r=>({
						key: r.reason_id,
						value: r.reason_id,
						text: r.reason,
					}))}
					style={{ marginTop: 10 }}
				/>
				<Message type="error" list={modalPrompts} style={{ marginTop: 15 }} />
			</Modal.Content>}
			<Modal.Actions>
				<Button text='Cancelar' basic onClick={()=>setDetailModal(DetailModals.BUDGET_INFO)} />
				<Button text='Rechazar' color='red' onClick={rejectBudget} loading={loadingAction} />
			</Modal.Actions>
		</Modal>
		<Modal open={detailModal===DetailModals.PAID} size='tiny' onClose={()=>setDetailModal(DetailModals.BUDGET_INFO)}>
			<Modal.Header>Marcar presupuesto como pagado</Modal.Header>
			{selectedBudget && <Modal.Content>
				<Header text='¿Marcar este presupuesto como pagado?' />
				<Table
					details
					striped
					data={[
						['ID', selectedBudget.budget_id],
						['Título', selectedBudget.budget_title],
						['Monto', '$' + addCommas(selectedBudget.amount)],
					]}
				/>
				<Message type="error" list={modalPrompts} style={{ marginTop: 15 }} />
			</Modal.Content>}
			<Modal.Actions>
				<Button text='Cancelar' basic onClick={()=>setDetailModal(DetailModals.BUDGET_INFO)} />
				<Button text='Marcar pagado' color='black' onClick={paid} loading={loadingAction} />
			</Modal.Actions>
		</Modal>
		<Modal open={detailModal===DetailModals.APPROVE_ALL_CONFIRM} size='tiny' onClose={()=>setDetailModal(null)}>
			<Modal.Header>Aprobar presupuestos</Modal.Header>
			<Modal.Content style={{ maxHeight: 500, overflowY: 'auto', overflowX: 'hidden' }}>
				<div style={{ textAlign: 'center' }}>
					<Button text='Seleccionar todos' color='black' onClick={()=>{
						setSelectedApproveIds(selectedApproveIds.length === pendingBudgets.length ? [] : pendingBudgets.map(b => b.budget_id));
					}}/>
				</div>
				<Table
					selectable
					fitted
					striped
					collapsingIndexes={[0, 1, 3]}
					centeredIndexes={[0]}
					style={{ marginTop: 15 }}
					onClick={(v: number[])=>{
						var arr = [...selectedApproveIds];
						var ix = arr.indexOf(v[1]);
						if(ix === -1) arr.push(v[1]);
						else arr.splice(ix, 1);
						setSelectedApproveIds(arr);
					}}
					headers={[
						<div style={{ textAlign: 'center' }}>
							<i className="checkmark icon" style={{ marginRight: 0 }}></i>
						</div>,
						'ID',
						'Título',
						'Monto',
					]}
					data={pendingBudgets.map(b=>{
						var selected = selectedApproveIds.indexOf(b.budget_id) !== -1;
						return [
							<i className={classNames('large icon circle', {
								check: selected,
								outline: !selected
							})}></i>,
							b.budget_id,
							b.budget_title,
							'$' + addCommas(b.amount),
						];
					})}
				/>
				<Table details style={{ marginTop: 15 }} data={[
					['Seleccionados', `${selectedApproveIds.length} de ${pendingBudgets.length}`],
					['Total', '$' + addCommas(selectedApproveTotal)],
				]}/>
				<Message type="error" list={modalPrompts} style={{ marginTop: 15 }} />
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cancelar' basic onClick={()=>setDetailModal(null)} />
				<Button text='Aprobar' color='black' onClick={approveAllBudgets} loading={loadingAction} disabled={selectedApproveIds.length === 0} />
			</Modal.Actions>
		</Modal>
		<Modal open={detailModal===DetailModals.PAID_ALL_CONFIRM} size='tiny' onClose={()=>setDetailModal(null)}>
			<Modal.Header>Marcar presupuestos como pagados</Modal.Header>
			<Modal.Content style={{ maxHeight: 500, overflowY: 'auto', overflowX: 'hidden' }}>
				<div style={{ textAlign: 'center' }}>
					<Button text='Seleccionar todos' color='black' onClick={()=>{
						setSelectedPaidIds(selectedPaidIds.length === payableBudgets.length ? [] : payableBudgets.map(b => b.budget_id));
					}}/>
				</div>
				<Table
					selectable
					fitted
					striped
					collapsingIndexes={[0, 1, 3]}
					centeredIndexes={[0]}
					style={{ marginTop: 15 }}
					onClick={(v: number[])=>{
						var arr = [...selectedPaidIds];
						var ix = arr.indexOf(v[1]);
						if(ix === -1) arr.push(v[1]);
						else arr.splice(ix, 1);
						setSelectedPaidIds(arr);
					}}
					headers={[
						<div style={{ textAlign: 'center' }}>
							<i className="checkmark icon" style={{ marginRight: 0 }}></i>
						</div>,
						'ID',
						'Título',
						'Monto',
					]}
					data={payableBudgets.map(b=>{
						var selected = selectedPaidIds.indexOf(b.budget_id) !== -1;
						return [
							<i className={classNames('large icon circle', {
								check: selected,
								outline: !selected
							})}></i>,
							b.budget_id,
							b.budget_title,
							'$' + addCommas(b.amount),
						];
					})}
				/>
				<Table details style={{ marginTop: 15 }} data={[
					['Seleccionados', `${selectedPaidIds.length} de ${payableBudgets.length}`],
					['Total', '$' + addCommas(selectedPaidTotal)],
				]}/>
				<Message type="error" list={modalPrompts} style={{ marginTop: 15 }} />
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cancelar' basic onClick={()=>setDetailModal(null)} />
				<Button text='Marcar pagados' color='black' onClick={paidAllBudgets} loading={loadingAction} disabled={selectedPaidIds.length === 0} />
			</Modal.Actions>
		</Modal>
	</div>
}

export default BudgetsDetails;